import { ComponentsVariants, isVariantOf } from '@/components.variants';
import ContentContainer from '@/features/shared/ContentContainer';
import DetailContent from '@/features/shared/DetailContent';
import { withDataLayerWrapper } from '@/features/shared/HOCs';
import { MuiContainer } from '@/features/shared/material-ui';
import EsTabbedContent from '@/features/shared/portal/EsTabbedContent';
import MyBusiness from '@/features/shared/portal/MyBusiness';
import { CaptureClicksRichText } from '@/features/shared/RichText';
import { animationDuration, ColorNames, SpacingSizes, Theme } from '@/utils/constants';
import clsx from 'clsx';
import React, { memo, useMemo, useRef } from 'react';
import { Fade } from 'react-reveal';
import { DashboardProps } from './Dashboard.interface';
import useStyles from './Dashboard.styles';
import { GmtTabbedListContent } from '@/features/shared/portal/TabbedListContent/TabbedListContent';

const mapVariantToProps = ({ variant }: any) => {
  if (/^formPicker$/.test(variant)) {
    return {
      containerColor: ColorNames.mediumGray,
    };
  }

  if (/^iconTextGrid$/.test(variant)) {
    return {
      orientation: 'column',
    };
  }

  // if (/^rtc$/.test(variant)) {
  //   return {
  //     wrapped: true,
  //     wrapperClassName: 'dashboard-card',
  //   };
  // }
  return {};
};

const mapVariantToClassName =
  (classes: any) =>
  ({ variant, props }: any) => {
    const alignmentMap: any = {
      top: classes.columnVerticalAlignmentTop,
      center: classes.columnVerticalAlignmentCenter,
      bottom: classes.columnVerticalAlignmentBottom,
    };

    if (/^rtc$/.test(variant)) {
      return classes.rtc;
    }

    if (/^horizontalButtonSet$/.test(variant)) {
      return classes.horizontalButtonSet;
    }

    if (/column/i.test(variant)) {
      return clsx(
        classes.columnContent,
        props?.verticalAlignment ? alignmentMap[props?.verticalAlignment] : '',
      );
    }

    if (/^formPicker$/.test(variant)) {
      return classes.form;
    }

    if (/^iconTextGrid$/.test(variant)) {
      return classes.iconTextGrid;
    }

    if (isVariantOf(ComponentsVariants.ES_TABBED_CONTENT)(variant)) {
      return classes.esTabbedListContent;
    }

    if (isVariantOf(ComponentsVariants.TABBED_LIST)(variant)) {
      return classes.tabbedListContent;
    }

    return '';
  };

const motionPreferenceReduced = () => {
  const mediaQueryList = window.matchMedia('(prefers-reduced-motion: no-preference');
  const prefersReducedMotion = !mediaQueryList.matches;
  return prefersReducedMotion;
};

type WithDashboardCardProps = {
  Component: any;
  wrapped?: boolean;
  overrideProps?: any;
};

const withDashboardCard =
  ({ Component, wrapped = true, overrideProps = {} }: WithDashboardCardProps) =>
  (props: any) => {
    const classes = useStyles({});

    return (
      <div className={wrapped ? clsx(classes.card, 'gntc-dashboard-card') : classes.noCard}>
        <Component {...props} {...overrideProps} />
      </div>
    );
  };

const Dashboard = ({
  detailContent = [],
  contentButtons,
  bottomSpacing,
  topSpacing,
}: Br.PropsWithContentButtons<DashboardProps>) => {
  const classes = useStyles({});

  // Used to override Detail Content component's list,
  // (!) only affects first level of Detail Content components
  // If want to override second level do next:
  //    DetailContent: withDashboardCard({
  //      Component: DetailContent,
  //      overrideProps: {
  //        components: { ... } // Second level
  //      }
  //    })
  const components = useMemo(
    () => ({
      DetailContent: withDataLayerWrapper({
        Component: withDashboardCard({ Component: DetailContent }),
        position: 'detail_content',
      }),
      CaptureClicksRichText: withDataLayerWrapper({
        Component: withDashboardCard({ Component: CaptureClicksRichText }),
        position: 'rich_text',
      }),
      MyBusiness: withDataLayerWrapper({
        Component: withDashboardCard({ Component: MyBusiness, overrideProps: { CtaColumns: 5 } }),
        position: 'my_business',
      }),
      ComplexTabList: withDataLayerWrapper({
        Component: withDashboardCard({ Component: GmtTabbedListContent }),
        position: 'complex_tab',
      }),
      EsTabbedContent: withDashboardCard({ Component: EsTabbedContent }),
    }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [],
  );

  const prefersReduced = useRef(motionPreferenceReduced());
  const rootClassName = clsx(classes.root, 'dark-theme', `aos-${prefersReduced.current}`);

  return (
    <ContentContainer
      topSpacing={topSpacing || SpacingSizes.none}
      bottomSpacing={bottomSpacing || SpacingSizes.none}
      topBackgroundColor={ColorNames.charcoal}
      bottomBackgroundColor={ColorNames.charcoal}
    >
      <MuiContainer>
        <section className={rootClassName}>
          {contentButtons?.content}
          <Fade duration={animationDuration.primary}>
            <div className={classes.content}>
              <DetailContent
                content={detailContent}
                theme={Theme.dark}
                mapVariantToProps={mapVariantToProps}
                mapVariantToClassName={mapVariantToClassName(classes)}
                className={classes.detailContent}
                components={components}
              />
            </div>
          </Fade>
        </section>
      </MuiContainer>
    </ContentContainer>
  );
};

export default memo(Dashboard);
